import { useMemo } from 'react'

import { useListCompanyArtifact } from '@/api/artifact.hook'
import type { Artifact } from '@/gen/artifacts/v1/model_pb'

import { cn } from '@/lib/ui/style-helpers'

import { CompanyArtifactTable } from '@/pages/company-drawer/artifact/company-artifact-table'
import { UploadFiles } from '@/pages/settings-page/artifacts-section/upload-files/upload-files'

// Above this many rows, natural-height rendering is too heavy; switch to a virtualised grid.
const MAX_ROWS_FOR_PAGE_SCROLL = 100

type ArtifactsTabProps = {
  /** Read-only snapshot rows, for finished-assessment reports. Ignored when live. */
  artifacts?: Artifact[]
  companyId: string
  companyName: string
  readonly?: boolean
}

export const ArtifactsTab = ({
  artifacts,
  companyId,
  companyName,
  readonly,
}: ArtifactsTabProps) => {
  const { data, isLoading } = useListCompanyArtifact(readonly ? undefined : companyId)
  const nonArchivedArtifacts = useMemo(
    () => data?.artifacts?.filter((a) => !a.archived) || [],
    [data],
  )
  const displayedArtifacts = readonly ? artifacts || [] : nonArchivedArtifacts
  const pageScroll = displayedArtifacts.length <= MAX_ROWS_FOR_PAGE_SCROLL

  return (
    <div className={cn('flex flex-col pb-6', pageScroll ? 'min-h-full' : 'h-full min-h-0')}>
      {!readonly && (
        <div className='mb-6'>
          <UploadFiles
            metadata={{ company: { id: companyId, name: companyName } }}
            immediateUpload
          />
        </div>
      )}
      <CompanyArtifactTable
        data={displayedArtifacts}
        isLoading={isLoading}
        readOnly={readonly}
        autoHeight={pageScroll}
        className={cn('flex-col', pageScroll ? 'flex' : 'flex min-h-0 flex-1')}
        containerClassName={pageScroll ? '' : 'h-full min-h-0 flex-1'}
        filterContext={`artifact_assessment_${companyId}`}
      />
    </div>
  )
}
